import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import App from './App';
import { store } from './reduxStore';
import './index.scss';
import DataProvider from './contexts/dataContext';
import {ScrollToTop} from "./hooks/useScrollToTop";
import * as Sentry from "@sentry/react";
import type { Integration } from "@sentry/core";
import moment from "moment-timezone";
import {markBoot} from "./services/startupTiming";
import {runWhenIdle} from "./services/idle";
import {attachReplayWhenIdle} from "./services/sentryReplay";
import {redactBreadcrumb, redactSpan} from "./services/sentryRedact";
import {initialLanguage, startWhenLanguageReady} from './locales';
import {handleUrlWithHashtag} from './GetUrlParams';
// import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'; // TODO: re-enable when BE reCaptcha is ready

markBoot(); // L13: entry module running — ms since navigation

const root = ReactDOM.createRoot(
    document.getElementById('twentyfourrent-rrm') as HTMLElement
);

let shortcodeLang: string | undefined;
let shortcodeCity: string | undefined;
let shortcodeMap: string | undefined;

// Session Replay continuously records DOM mutations — a measurable main-thread cost on
// mobile CPUs, where INP already fails site-wide (Search Console 8/2026). Microsoft
// Clarity still records mobile sessions, so keep Sentry Replay desktop-only.
const smallScreen = window.matchMedia('(max-width: 767px)').matches;

const sentryIntegrations: Integration[] = [
    Sentry.browserTracingIntegration({enableInp: true}),
];
// Replay is added after load and idle, not at init: constructing it here put its
// DOM recorder on the startup path of every desktop view, ahead of the first
// render (improvement row L10) — and since row I2 the recorder is not in the entry
// chunk at all: it is loaded from Sentry's CDN when wanted (services/sentryReplay.ts).
// The sample rates on the init below still apply — the integration reads them from
// the client when it is added. Accepted cost: an error thrown before load + idle is
// still reported, but carries no replay (replaysOnErrorSampleRate needs the recorder
// to be running before the error).
// Pass the two functions, not the `Sentry` namespace: handing the namespace object to a
// function makes Rollup keep every export in the entry — replayIntegration included — and
// the build gate (check-deferred-build.mjs) fails. Verified both ways on 6.10.2026.
attachReplayWhenIdle({lazyLoadIntegration: Sentry.lazyLoadIntegration, addIntegration: Sentry.addIntegration}, runWhenIdle, smallScreen);

Sentry.setUser({username: window.location.host}); // can add own temp username here if monitoring on Sentry
Sentry.init({
    dsn: 'https://16fdf705703a95de8fcf871151f16d0a@o1120901.ingest.us.sentry.io/4507260438183936',
    // this assumes your build process replaces `process.env.npm_package_version` with a value
    integrations: sentryIntegrations,

    tracesSampleRate: 1.0,
    // Request addresses can carry secrets (the Walley status check sends a checkout token in its query):
    // scrub them from breadcrumbs and spans before they are sent. Replay does the same (services/sentryReplay.ts).
    beforeBreadcrumb: redactBreadcrumb,
    beforeSendSpan: redactSpan,
    replaysSessionSampleRate: smallScreen ? 0 : 0.1,
    replaysOnErrorSampleRate: smallScreen ? 0 : 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [ /^https:\/\/dev-rent\.24rent\.fi/,  /^https:\/\/rent\.24rent\.fi/,  /^https:\/\/www\.24rent\.fi/,  /^https:\/\/dev-frontend\.24rent\.fi/],
});

moment.tz.setDefault('Europe/Helsinki');

// checks environment to avoid run error
// the global wp params will only be available in a shortcode compatible environment (like word press sites)
if ((window as any).shortcode_parameters) {
    shortcodeLang = (window as any).shortcode_parameters.lang;
    shortcodeCity = (window as any).shortcode_parameters.city;
    shortcodeMap = (window as any).shortcode_parameters.map;
}

sessionStorage.removeItem('starsUpdatedDS'); // remove if exists
sessionStorage.removeItem('acrissCodesUpdatedDS'); // remove if exists

const render = () => root.render(
    // {/* TODO: re-enable GoogleReCaptchaProvider when BE reCaptcha is ready */}
    // <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? ''}>
        <Provider store={store}>
            <DataProvider>
                <BrowserRouter>
                    <ScrollToTop >
                        <App shortcodeLang={shortcodeLang} shortcodeCity={shortcodeCity}
                             shortcodeMap={shortcodeMap}/>
                    </ScrollToTop>
                </BrowserRouter>
            </DataProvider>
        </Provider>
    // </GoogleReCaptchaProvider>
);
// Only Finnish is in this chunk (row I2). An English page registers its translations first —
// one lazy chunk, preloaded from the head on lang="en" pages — so the first frame is already
// English; Finnish renders at once. If the English chunk cannot load, App's language effect
// settles on Finnish rather than leaving raw keys on screen. If it is slow, the widget renders
// in Finnish after LANGUAGE_WAIT_MS and App switches to English when the chunk arrives.
// handleUrlWithHashtag: App reads ?lang= through it too, so a legacy /#/?lang=en link is English here as well.
startWhenLanguageReady(initialLanguage(handleUrlWithHashtag(), shortcodeLang), render);
